import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  MenuItem,
  Chip,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const Users = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    role: 'employee',
    status: 'active',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users');
      setUsers(response.data.users);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setFormData({ username: '', role: 'employee', status: 'active' });
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({ username: '', role: 'employee', status: 'active' });
    setError('');
  };

  const handleEditOpen = (user) => {
    setSelectedUser(user);
    setFormData({ 
      username: user.username, 
      role: user.role, 
      status: user.status || 'active' 
    });
    setEditDialogOpen(true);
    setError('');
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedUser(null);
    setFormData({ username: '', role: 'employee', status: 'active' });
    setError('');
  };

  const handleDeleteOpen = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setSelectedUser(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await axios.post('/api/users', formData);
      setSuccess('Пользователь успешно создан');
      fetchUsers();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка создания пользователя');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await axios.put(`/api/users/${selectedUser.uuid}`, formData);
      setSuccess('Пользователь успешно обновлен');
      handleEditClose();
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка обновления пользователя');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;

    try {
      await axios.delete(`/api/users/${selectedUser.uuid}`);
      setSuccess('Пользователь удален');
      handleDeleteClose();
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка удаления пользователя');
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin':
        return 'Супер Админ';
      case 'admin':
        return 'Администратор';
      default:
        return 'Сотрудник';
    }
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'success' : 'error';
  };

  const getStatusLabel = (status) => {
    return status === 'active' ? 'Активен' : 'Неактивен';
  };

  const canEditUser = (targetUser) => {
    if (user?.role === 'super_admin') return true;
    if (user?.role === 'admin' && targetUser.role !== 'super_admin' && targetUser.role !== 'admin') return true;
    return false;
  };

  const canDeleteUser = (targetUser) => {
    return user?.role === 'super_admin';
  };

  return (
    <Layout>
      <Container maxWidth="lg">
        <Box sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" component="h1">
            Управление пользователями
          </Typography>
          {(user?.role === 'admin' || user?.role === 'super_admin') && (
            <Button variant="contained" onClick={handleOpen}>
              Добавить пользователя
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>UUID</TableCell>
                <TableCell>Имя пользователя</TableCell>
                <TableCell>Роль</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Telegram</TableCell>
                <TableCell>Дата создания</TableCell>
                <TableCell>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => (
                <TableRow 
                  key={u.uuid}
                  sx={{ 
                    opacity: u.status !== 'active' ? 0.6 : 1,
                    backgroundColor: u.status !== 'active' ? 'action.hover' : 'transparent'
                  }}
                >
                  <TableCell>{u.uuid}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: u.status !== 'active' ? 400 : 500 }}>
                        {u.username}
                      </Typography>
                      {u.status !== 'active' && (
                        <Chip label="Неактивен" size="small" color="error" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{getRoleLabel(u.role)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={getStatusLabel(u.status)} 
                      color={getStatusColor(u.status)} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>
                    {u.telegram_username ? (
                      <Typography variant="body2" color="text.secondary">
                        @{u.telegram_username}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Не подключен
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(u.created_at).toLocaleDateString('ru-RU')}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {canEditUser(u) && (
                        <IconButton
                          size="small"
                          onClick={() => handleEditOpen(u)}
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                      {canDeleteUser(u) && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteOpen(u)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Диалог создания пользователя */}
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
          <form onSubmit={handleSubmit}>
            <DialogTitle>Добавить пользователя</DialogTitle>
            <DialogContent>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <TextField
                fullWidth
                label="Имя пользователя"
                value={formData.username}
                onChange={(e) =>
                  setFormData({ ...formData, username: e.target.value })
                }
                margin="normal"
                required
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Роль</InputLabel>
                <Select
                  value={formData.role}
                  onChange={(e) =>
                    setFormData({ ...formData, role: e.target.value })
                  }
                  label="Роль"
                >
                  <MenuItem value="employee">Сотрудник</MenuItem>
                  {user?.role === 'super_admin' && (
                    <>
                      <MenuItem value="admin">Администратор</MenuItem>
                      <MenuItem value="super_admin">Супер Админ</MenuItem>
                    </>
                  )}
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal">
                <InputLabel>Статус</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                  label="Статус"
                >
                  <MenuItem value="active">Активен</MenuItem>
                  <MenuItem value="inactive">Неактивен</MenuItem>
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>Отмена</Button>
              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? 'Создание...' : 'Создать'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Диалог редактирования пользователя */}
        <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
          <form onSubmit={handleEdit}>
            <DialogTitle>Редактировать пользователя</DialogTitle>
            <DialogContent>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <TextField
                fullWidth
                label="Имя пользователя"
                value={formData.username}
                onChange={(e) =>
                  setFormData({ ...formData, username: e.target.value })
                }
                margin="normal"
                required
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Роль</InputLabel>
                <Select
                  value={formData.role}
                  onChange={(e) =>
                    setFormData({ ...formData, role: e.target.value })
                  }
                  label="Роль"
                  disabled={selectedUser?.role === 'super_admin' && user?.role !== 'super_admin'}
                >
                  <MenuItem value="employee">Сотрудник</MenuItem>
                  {user?.role === 'super_admin' && (
                    <>
                      <MenuItem value="admin">Администратор</MenuItem>
                      <MenuItem value="super_admin">Супер Админ</MenuItem>
                    </>
                  )}
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal">
                <InputLabel>Статус</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                  label="Статус"
                >
                  <MenuItem value="active">Активен</MenuItem>
                  <MenuItem value="inactive">Неактивен</MenuItem>
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleEditClose}>Отмена</Button>
              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? 'Сохранение...' : 'Сохранить'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Диалог удаления пользователя */}
        <Dialog open={deleteDialogOpen} onClose={handleDeleteClose}>
          <DialogTitle>Удалить пользователя?</DialogTitle>
          <DialogContent>
            <Typography>
              Вы уверены, что хотите удалить пользователя "{selectedUser?.username}"?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteClose}>Отмена</Button>
            <Button onClick={handleDelete} variant="contained" color="error">
              Удалить
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default Users;
